const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => resolve(img);
        img.onerror = () =>
            reject(new Error("Could not load the image for cropping."));

        img.src = src;
    });

/**
 * Crops (and optionally rotates) an image.
 *
 * If outWidth / outHeight are given, the result is always exactly that size
 * (the selected area is scaled to fit), so zooming beyond the original
 * resolution still produces a correctly sized, if softer, file.
 * Without them, the output keeps the crop's source-pixel size.
 */
export const cropImage = async (
    imageSrc,
    pixelCrop,
    mimeType = "image/png",
    quality = 0.95,
    rotation = 0,
    outWidth,
    outHeight
) => {
    const img = await loadImage(imageSrc);

    const radians = (rotation * Math.PI) / 180;

    // Bounding box of the rotated image
    const rotatedWidth =
        Math.abs(img.naturalWidth * Math.cos(radians)) +
        Math.abs(img.naturalHeight * Math.sin(radians));

    const rotatedHeight =
        Math.abs(img.naturalWidth * Math.sin(radians)) +
        Math.abs(img.naturalHeight * Math.cos(radians));

    // Canvas containing the rotated image
    const rotatedCanvas = document.createElement("canvas");
    rotatedCanvas.width = Math.ceil(rotatedWidth);
    rotatedCanvas.height = Math.ceil(rotatedHeight);

    const rotatedCtx = rotatedCanvas.getContext("2d");
    rotatedCtx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
    rotatedCtx.rotate(radians);
    rotatedCtx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

    const cropW = Math.round(pixelCrop.width);
    const cropH = Math.round(pixelCrop.height);

    if (cropW <= 0 || cropH <= 0) {
        throw new Error("Invalid crop area. Please adjust your selection.");
    }

    const targetW = Math.round(outWidth || cropW);
    const targetH = Math.round(outHeight || cropH);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;

    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // JPEG has no transparency, so blank areas (e.g. rotated corners) become white
    if (mimeType === "image/jpeg") {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, targetW, targetH);
    }

    // Draw the whole rotated image shifted and scaled so the crop area fills the
    // output. Parts of the crop outside the image stay blank instead of being
    // stretched, so the picture is never distorted.
    const scaleX = targetW / cropW;
    const scaleY = targetH / cropH;

    ctx.drawImage(
        rotatedCanvas,
        -Math.round(pixelCrop.x) * scaleX,
        -Math.round(pixelCrop.y) * scaleY,
        rotatedCanvas.width * scaleX,
        rotatedCanvas.height * scaleY
    );

    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error("Failed to export the cropped image."));
                }
            },
            mimeType,
            quality
        );
    });
};

export const blobToFile = (blob, originalName = "image") => {
    const ext =
        {
            "image/jpeg": "jpg",
            "image/png": "png",
            "image/webp": "webp",
        }[blob.type] || "png";

    const base = originalName.replace(/\.[^.]+$/, "");

    return new File([blob], `${base}.${ext}`, { type: blob.type });
};